type EventLevel = "error" | "warn" | "debug";

export type TelemetryType =
  | "audio_ok"
  | "audio_failed"
  | "audio_pipeline_degraded"
  | "autoplay_blocked"
  | "speech_ok"
  | "speech_no_onstart"
  | "speech_no_onend"
  | "speech_error"
  | "speech_skipped_hidden"
  | "socket_connect"
  | "socket_disconnect"
  | "socket_connect_error"
  | "socket_reconnect_attempt"
  | "socket_reconnect"
  | "socket_reconnect_failed"
  | "rehydrate_used"
  | "rehydrate_failed"
  | "reload_skipped_active_call"
  | "reload_executed"
  | "reload_suspended_after_recent"
  | "visibility_change"
  | "net_online"
  | "net_offline"
  | "dedup_block"
  | "boot";

interface TelemetryEvent {
  type: TelemetryType;
  data?: any;
  ts: number;
  panelId: string | null;
}

const RING_SIZE = 200;
const buffer: TelemetryEvent[] = [];

const CRITICAL: Set<TelemetryType> = new Set([
  "audio_failed",
  "audio_pipeline_degraded",
  "autoplay_blocked",
  "speech_no_onstart",
  "speech_no_onend",
  "speech_error",
  "socket_connect_error",
  "socket_disconnect",
  "socket_reconnect_failed",
  "rehydrate_failed",
]);

const NOISY: Set<TelemetryType> = new Set([
  "audio_ok",
  "speech_ok",
  "dedup_block",
  "visibility_change",
]);

function getPanelId(): string | null {
  try {
    const raw = localStorage.getItem("panel@drclick");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.idpainelchamado ?? null;
  } catch {
    return null;
  }
}

function levelFor(type: TelemetryType): EventLevel {
  if (CRITICAL.has(type)) return "error";
  if (NOISY.has(type)) return "debug";
  return "warn";
}

export function telemetry(type: TelemetryType, data?: any): void {
  const ev: TelemetryEvent = {
    type,
    data,
    ts: Date.now(),
    panelId: getPanelId(),
  };
  buffer.push(ev);
  if (buffer.length > RING_SIZE) buffer.shift();

  const level = levelFor(type);
  const tag = `[panel:${type}]`;
  if (level === "error") console.error(tag, data ?? "");
  else if (level === "warn") console.warn(tag, data ?? "");
  else console.debug(tag, data ?? "");
}

function ring(): TelemetryEvent[] {
  return buffer.slice();
}

function clear(): void {
  buffer.length = 0;
}

function summary(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const ev of buffer) counts[ev.type] = (counts[ev.type] || 0) + 1;
  return counts;
}

if (typeof window !== "undefined") {
  (window as any).__panelDebug = { ring, clear, summary };
}
